import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Container, Ruler, Building2, Plus, FileText, FileCheck, DollarSign,
  RefreshCw, Truck, Calculator, CreditCard, Banknote, Clock, CheckCircle,
  Download, Eye, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONCEPTOS_DEFAULT = [
  { clave: 'llenadores_furgon', nombre: 'Llenadores Furgon', moneda: 'USD' },
  { clave: 'transporte_usa', nombre: 'Transporte Furgon USA', moneda: 'USD' },
  { clave: 'booking', nombre: 'Booking', moneda: 'USD' },
  { clave: 'aduana_dgi', nombre: 'Aduana DGI', moneda: 'DOP' },
  { clave: 'verificacion_aduana', nombre: 'Verificacion Aduana', moneda: 'DOP' },
  { clave: 'distribucion', nombre: 'Distribucion', moneda: 'DOP' },
];

const RentaEspacio = () => {
  const [furgones, setFurgones] = useState([]);
  const [companias, setCompanias] = useState([]);
  const [selectedFurgon, setSelectedFurgon] = useState('');
  const [calculadora, setCalculadora] = useState(null);
  const [rentas, setRentas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [companiaDialog, setCompaniaDialog] = useState(false);
  const [rentaDialog, setRentaDialog] = useState(false);
  const [pagoDialog, setPagoDialog] = useState({ open: false, renta: null });
  const [blDialog, setBlDialog] = useState(false);
  const [detalleDialog, setDetalleDialog] = useState({ open: false, renta: null });

  // Forms
  const [companiaForm, setCompaniaForm] = useState({ nombre: '', contacto: '', telefono: '', email: '', direccion: '', cedula_rnc: '' });
  const [rentaForm, setRentaForm] = useState({ compania_id: '', pies_rentados: '', conceptos: CONCEPTOS_DEFAULT.map(c => ({ ...c, monto: 0 })), notas: '' });
  const [pagoForm, setPagoForm] = useState({ concepto_clave: '', monto: 0, moneda: 'USD', metodo_pago: 'efectivo', notas: '' });
  const [blForm, setBlForm] = useState('');

  useEffect(() => {
    fetchFurgones();
    fetchCompanias();
  }, []);

  const fetchFurgones = async () => {
    try {
      const res = await api.get('/furgones');
      setFurgones(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchCompanias = async () => {
    try {
      const res = await api.get('/rentas/companias');
      setCompanias(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchData = useCallback(async () => {
    if (!selectedFurgon) return;
    setLoading(true);
    try {
      const furgon = furgones.find(f => f.id === selectedFurgon);
      const [calcRes, rentasRes] = await Promise.all([
        api.get(`/rentas/calculadora/${selectedFurgon}`),
        api.get('/rentas/espacios', { params: { furgon_id: selectedFurgon } }),
      ]);
      setCalculadora(calcRes.data);
      setRentas(rentasRes.data || []);
    } catch (e) { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, [selectedFurgon, furgones]);

  useEffect(() => { if (selectedFurgon) fetchData(); }, [selectedFurgon, fetchData]);

  const furgonActual = furgones.find(f => f.id === selectedFurgon);

  // === HANDLERS ===
  const handleCrearCompania = async (e) => {
    e.preventDefault();
    if (!companiaForm.nombre) { toast.error('Nombre requerido'); return; }
    try {
      await api.post('/rentas/companias', companiaForm);
      toast.success('Compania registrada');
      setCompaniaDialog(false);
      setCompaniaForm({ nombre: '', contacto: '', telefono: '', email: '', direccion: '', cedula_rnc: '' });
      fetchCompanias();
    } catch (e) { toast.error('Error al crear compania'); }
  };

  const handleCrearRenta = async (e) => {
    e.preventDefault();
    if (!rentaForm.compania_id || !rentaForm.pies_rentados) { toast.error('Completa los campos requeridos'); return; }
    try {
      const payload = {
        furgon_id: selectedFurgon,
        compania_id: rentaForm.compania_id,
        pies_rentados: parseFloat(rentaForm.pies_rentados),
        conceptos: rentaForm.conceptos.filter(c => c.monto > 0),
        notas: rentaForm.notas,
      };
      await api.post('/rentas/espacios', payload);
      toast.success('Renta creada exitosamente');
      setRentaDialog(false);
      setRentaForm({ compania_id: '', pies_rentados: '', conceptos: CONCEPTOS_DEFAULT.map(c => ({ ...c, monto: 0 })), notas: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear renta'); }
  };

  const handlePago = async (e) => {
    e.preventDefault();
    if (!pagoForm.concepto_clave || pagoForm.monto <= 0) { toast.error('Completa los campos'); return; }
    try {
      await api.post('/rentas/pagos', { renta_id: pagoDialog.renta.id, ...pagoForm });
      toast.success('Pago registrado');
      setPagoDialog({ open: false, renta: null });
      setPagoForm({ concepto_clave: '', monto: 0, moneda: 'USD', metodo_pago: 'efectivo', notas: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleBL = async () => {
    if (!blForm) { toast.error('Ingresa el numero B/L'); return; }
    try {
      await api.patch(`/rentas/furgon/${selectedFurgon}/bl`, { numero_bl: blForm });
      toast.success('B/L actualizado');
      setBlDialog(false);
      setBlForm('');
      fetchFurgones();
      fetchData();
    } catch (e) { toast.error('Error'); }
  };

  const updateConcepto = (idx, field, value) => {
    setRentaForm(prev => {
      const conceptos = [...prev.conceptos];
      conceptos[idx] = { ...conceptos[idx], [field]: field === 'monto' ? parseFloat(value) || 0 : value };
      return { ...prev, conceptos };
    });
  };

  const openPagoDialog = (renta, concepto) => {
    setPagoForm({ concepto_clave: concepto.clave, monto: concepto.monto - (concepto.pagado || 0), moneda: concepto.moneda, metodo_pago: 'efectivo', notas: '' });
    setPagoDialog({ open: true, renta });
  };

  const downloadPdf = (type, rentaId) => {
    window.open(`${API_URL}/api/rentas/${type}/${rentaId}`, '_blank');
  };

  const calc = calculadora || {};
  const porcentaje = calc.porcentaje_ocupado || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50 p-3 sm:p-6 pb-24" data-testid="renta-espacio-page">
      <div className="max-w-7xl mx-auto space-y-4">

        {/* HEADER */}
        <Card className="shadow-xl border-2 border-amber-100">
          <CardHeader className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-t-lg py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg"><Container className="h-7 w-7" /></div>
                <div>
                  <CardTitle className="text-2xl font-bold">Renta de Espacio</CardTitle>
                  <p className="text-amber-200 text-sm">Control por Furgon - Pies, Facturas y Contratos</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={selectedFurgon} onValueChange={setSelectedFurgon}>
                  <SelectTrigger data-testid="select-furgon-renta" className="h-10 w-52 bg-white/20 border-white/30 text-white">
                    <SelectValue placeholder="Seleccionar Furgon..." />
                  </SelectTrigger>
                  <SelectContent>
                    {furgones.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.numero} ({f.capacidad_pies || 40}ft) {f.numero_contenedor ? `- ${f.numero_contenedor}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button data-testid="btn-nueva-compania" onClick={() => setCompaniaDialog(true)} size="sm" className="h-10 bg-white/20 text-white hover:bg-white/30 border-white/30" variant="outline">
                  <Building2 className="h-4 w-4 mr-1" /> Compania
                </Button>
                {selectedFurgon && (
                  <Button data-testid="refresh-renta" variant="secondary" size="sm" onClick={fetchData} className="h-10 bg-white/20 text-white hover:bg-white/30 border-white/30">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {!selectedFurgon ? (
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="py-16 text-center">
              <Truck className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg text-gray-500 font-medium">Selecciona un furgon para gestionar rentas</p>
              <p className="text-sm text-gray-400">Puedes registrar companias primero con el boton arriba</p>
            </CardContent>
          </Card>
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
          </div>
        ) : (
          <>
            {/* FURGON INFO + B/L */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card className="border-2 border-gray-200">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-400 font-bold uppercase">Ref. Interna</p>
                  <p className="text-xl font-black text-gray-800">{furgonActual?.numero}</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-200">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-400 font-bold uppercase">Contenedor Real</p>
                  <p className="text-xl font-black text-gray-800">{furgonActual?.numero_contenedor || <span className="text-gray-300">Sin asignar</span>}</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-blue-200 cursor-pointer hover:bg-blue-50 transition" onClick={() => { setBlForm(furgonActual?.numero_bl || ''); setBlDialog(true); }}>
                <CardContent className="p-4">
                  <p className="text-xs text-blue-500 font-bold uppercase">B/L (Bill of Lading)</p>
                  <p className="text-xl font-black text-blue-700">{furgonActual?.numero_bl || <span className="text-blue-300">Click para ingresar</span>}</p>
                </CardContent>
              </Card>
            </div>

            {/* CALCULATOR BAR */}
            <Card className="border-2 border-amber-200" data-testid="calculadora-pies">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-amber-600" />
                    <span className="font-bold text-gray-800">Calculadora de Pies</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-500">Capacidad: </span>
                    <span className="text-lg font-black text-gray-800">{calc.capacidad_pies || 40} pies</span>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="relative h-10 bg-gray-100 rounded-full overflow-hidden mb-3">
                  <div
                    className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${porcentaje > 90 ? 'bg-red-500' : porcentaje > 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                    style={{ width: `${Math.min(porcentaje, 100)}%` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-black text-gray-800 drop-shadow-sm">
                      {calc.pies_ocupados || 0} / {calc.capacidad_pies || 40} pies ({porcentaje}%)
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-2 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-xs text-green-600 font-bold">DISPONIBLE</p>
                    <p className="text-2xl font-black text-green-700">{calc.pies_disponibles || calc.capacidad_pies || 40}</p>
                  </div>
                  <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-600 font-bold">OCUPADO</p>
                    <p className="text-2xl font-black text-amber-700">{calc.pies_ocupados || 0}</p>
                  </div>
                  <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600 font-bold">RENTAS</p>
                    <p className="text-2xl font-black text-blue-700">{calc.desglose?.length || 0}</p>
                  </div>
                </div>
                {/* Desglose */}
                {calc.desglose?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {calc.desglose.map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="font-semibold">{d.compania}</span>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-xs">{d.numero_factura}</Badge>
                          <span className="font-bold text-amber-600">{d.pies} pies</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* NEW RENTA BUTTON */}
            <Button data-testid="btn-nueva-renta" onClick={() => setRentaDialog(true)} className="w-full h-14 bg-amber-600 hover:bg-amber-700 text-white font-bold text-base">
              <Plus className="mr-2 h-5 w-5" /> Nueva Renta de Espacio
            </Button>

            {/* RENTAS TABLE */}
            <Card className="border-2 border-amber-100">
              <CardHeader className="py-3 bg-amber-50 border-b border-amber-100">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                  <FileText className="h-5 w-5" /> Rentas del Furgon ({rentas.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {rentas.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Container className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>No hay rentas para este furgon</p>
                  </div>
                ) : (
                  <div className="space-y-4 p-4">
                    {rentas.map((r) => (
                      <RentaCard
                        key={r.id}
                        renta={r}
                        onPago={(concepto) => openPagoDialog(r, concepto)}
                        onDetalle={() => setDetalleDialog({ open: true, renta: r })}
                        onContrato={() => downloadPdf('contrato', r.id)}
                        onFactura={() => downloadPdf('factura', r.id)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* COMPANIAS REGISTRADAS */}
            {companias.length > 0 && (
              <Card className="border-2 border-gray-200">
                <CardHeader className="py-3 bg-gray-50 border-b">
                  <CardTitle className="text-base flex items-center gap-2 text-gray-700">
                    <Users className="h-5 w-5" /> Companias Registradas ({companias.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-bold text-xs">Nombre</TableHead>
                        <TableHead className="font-bold text-xs">Contacto</TableHead>
                        <TableHead className="font-bold text-xs">Telefono</TableHead>
                        <TableHead className="font-bold text-xs">RNC/Cedula</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companias.map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="font-bold">{c.nombre}</TableCell>
                          <TableCell className="text-sm">{c.contacto || '-'}</TableCell>
                          <TableCell className="text-sm">{c.telefono || '-'}</TableCell>
                          <TableCell className="text-sm">{c.cedula_rnc || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ========= DIALOGS ========= */}

        {/* NUEVA COMPANIA */}
        <Dialog open={companiaDialog} onOpenChange={setCompaniaDialog}>
          <DialogContent className="max-w-md" data-testid="compania-dialog" aria-describedby="comp-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-amber-600" /> Registrar Compania</DialogTitle>
              <p id="comp-desc" className="text-sm text-gray-500">Nueva compania que renta espacio en tus furgones</p>
            </DialogHeader>
            <form onSubmit={handleCrearCompania} className="space-y-3">
              <div><Label className="font-semibold">Nombre *</Label><Input data-testid="comp-nombre" value={companiaForm.nombre} onChange={(e) => setCompaniaForm(p => ({...p, nombre: e.target.value}))} required className="h-11 mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-semibold">Contacto</Label><Input data-testid="comp-contacto" value={companiaForm.contacto} onChange={(e) => setCompaniaForm(p => ({...p, contacto: e.target.value}))} className="h-11 mt-1" /></div>
                <div><Label className="font-semibold">Telefono</Label><Input data-testid="comp-telefono" value={companiaForm.telefono} onChange={(e) => setCompaniaForm(p => ({...p, telefono: e.target.value}))} className="h-11 mt-1" /></div>
              </div>
              <div><Label className="font-semibold">RNC / Cedula</Label><Input data-testid="comp-rnc" value={companiaForm.cedula_rnc} onChange={(e) => setCompaniaForm(p => ({...p, cedula_rnc: e.target.value}))} className="h-11 mt-1" /></div>
              <div><Label className="font-semibold">Direccion</Label><Input value={companiaForm.direccion} onChange={(e) => setCompaniaForm(p => ({...p, direccion: e.target.value}))} className="h-11 mt-1" /></div>
              <Button data-testid="submit-compania" type="submit" className="w-full h-11 bg-amber-600 hover:bg-amber-700">
                <Building2 className="mr-2 h-4 w-4" /> Registrar Compania
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* NUEVA RENTA */}
        <Dialog open={rentaDialog} onOpenChange={setRentaDialog}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="renta-dialog" aria-describedby="renta-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Ruler className="h-5 w-5 text-amber-600" /> Nueva Renta de Espacio</DialogTitle>
              <p id="renta-desc" className="text-sm text-gray-500">Furgon: {furgonActual?.numero} | Disponible: {calc.pies_disponibles} pies</p>
            </DialogHeader>
            <form onSubmit={handleCrearRenta} className="space-y-4">
              <div>
                <Label className="font-semibold">Compania *</Label>
                <Select value={rentaForm.compania_id} onValueChange={(v) => setRentaForm(p => ({...p, compania_id: v}))}>
                  <SelectTrigger data-testid="renta-compania" className="h-11 mt-1"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {companias.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-semibold">Pies a Rentar *</Label>
                <Input data-testid="renta-pies" type="number" step="0.1" min="0.1" value={rentaForm.pies_rentados} onChange={(e) => setRentaForm(p => ({...p, pies_rentados: e.target.value}))} placeholder="Ej: 5.7, 8.6, 15.9" className="h-11 mt-1 text-lg font-bold" required />
              </div>

              <div>
                <Label className="font-semibold text-sm">Desglose de Costos</Label>
                <div className="space-y-2 mt-2">
                  {rentaForm.conceptos.map((c, i) => (
                    <div key={c.clave} className="flex items-center gap-2">
                      <span className="text-xs font-semibold w-36 truncate">{c.nombre}</span>
                      <Badge variant="outline" className="text-xs w-12 justify-center">{c.moneda}</Badge>
                      <Input
                        type="number" step="0.01" min="0"
                        value={c.monto || ''}
                        onChange={(e) => updateConcepto(i, 'monto', e.target.value)}
                        placeholder="0.00"
                        className="h-9 flex-1 text-right font-bold"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-2 p-2 bg-gray-50 rounded-lg flex justify-between text-sm">
                  <span>Total USD: <b className="text-green-600">${rentaForm.conceptos.filter(c => c.moneda === 'USD').reduce((s, c) => s + (c.monto || 0), 0).toFixed(2)}</b></span>
                  <span>Total DOP: <b className="text-blue-600">${rentaForm.conceptos.filter(c => c.moneda === 'DOP').reduce((s, c) => s + (c.monto || 0), 0).toFixed(2)}</b></span>
                </div>
              </div>

              <div><Label className="font-semibold">Notas</Label><Input value={rentaForm.notas} onChange={(e) => setRentaForm(p => ({...p, notas: e.target.value}))} placeholder="Notas opcionales" className="h-11 mt-1" /></div>

              <Button data-testid="submit-renta" type="submit" className="w-full h-11 bg-amber-600 hover:bg-amber-700">
                <Plus className="mr-2 h-4 w-4" /> Crear Renta
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* PAGO DIALOG */}
        <Dialog open={pagoDialog.open} onOpenChange={(open) => setPagoDialog({ open, renta: pagoDialog.renta })}>
          <DialogContent className="max-w-md" data-testid="pago-dialog" aria-describedby="pago-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-green-600" /> Registrar Pago</DialogTitle>
              <p id="pago-desc" className="text-sm text-gray-500">{pagoDialog.renta?.compania_nombre} - {pagoDialog.renta?.numero_factura}</p>
            </DialogHeader>
            <form onSubmit={handlePago} className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p>Concepto: <b>{pagoForm.concepto_clave}</b></p>
                <p>Moneda: <b>{pagoForm.moneda}</b></p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-semibold">Monto *</Label><Input data-testid="pago-monto" type="number" step="0.01" min="0.01" value={pagoForm.monto || ''} onChange={(e) => setPagoForm(p => ({...p, monto: parseFloat(e.target.value) || 0}))} className="h-11 mt-1" /></div>
                <div>
                  <Label className="font-semibold">Metodo Pago</Label>
                  <Select value={pagoForm.metodo_pago} onValueChange={(v) => setPagoForm(p => ({...p, metodo_pago: v}))}>
                    <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">Efectivo USD</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="efectivo_rd">Efectivo RD (DOP)</SelectItem>
                      <SelectItem value="transferencia_rd">Transferencia RD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="submit-pago" type="submit" className="w-full h-11 bg-green-600 hover:bg-green-700">
                <DollarSign className="mr-2 h-4 w-4" /> Registrar Pago
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* B/L DIALOG */}
        <Dialog open={blDialog} onOpenChange={setBlDialog}>
          <DialogContent className="max-w-sm" data-testid="bl-dialog" aria-describedby="bl-desc">
            <DialogHeader>
              <DialogTitle>Ingresar B/L (Bill of Lading)</DialogTitle>
              <p id="bl-desc" className="text-sm text-gray-500">Este numero aparecera en los contratos</p>
            </DialogHeader>
            <div className="space-y-4">
              <Input data-testid="bl-input" value={blForm} onChange={(e) => setBlForm(e.target.value)} placeholder="Numero B/L" className="h-11 text-lg font-bold" />
              <Button data-testid="submit-bl" onClick={handleBL} className="w-full h-11 bg-blue-600 hover:bg-blue-700">Guardar B/L</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* DETALLE RENTA DIALOG */}
        <Dialog open={detalleDialog.open} onOpenChange={(open) => setDetalleDialog({ open, renta: detalleDialog.renta })}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="detalle-dialog" aria-describedby="det-desc">
            <DialogHeader>
              <DialogTitle>{detalleDialog.renta?.numero_factura} - {detalleDialog.renta?.compania_nombre}</DialogTitle>
              <p id="det-desc" className="text-sm text-gray-500">Pies: {detalleDialog.renta?.pies_rentados} | Furgon: {detalleDialog.renta?.furgon_numero}</p>
            </DialogHeader>
            {detalleDialog.renta && (
              <div className="space-y-4">
                <Table>
                  <TableHeader><TableRow><TableHead>Concepto</TableHead><TableHead>Moneda</TableHead><TableHead className="text-right">Monto</TableHead><TableHead className="text-right">Pagado</TableHead><TableHead className="text-right">Pendiente</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {detalleDialog.renta.conceptos?.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-semibold">{c.nombre}</TableCell>
                        <TableCell><Badge variant="outline">{c.moneda}</Badge></TableCell>
                        <TableCell className="text-right font-bold">${c.monto?.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-bold text-green-600">${(c.pagado || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-bold text-red-600">${Math.max(0, c.monto - (c.pagado || 0)).toFixed(2)}</TableCell>
                        <TableCell><Badge className={c.estado === 'pagado' ? 'bg-green-100 text-green-800' : c.estado === 'parcial' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>{c.estado?.toUpperCase()}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {detalleDialog.renta.pagos?.length > 0 && (
                  <>
                    <h4 className="font-bold text-sm">Historial de Pagos</h4>
                    {detalleDialog.renta.pagos.map((p, i) => (
                      <div key={i} className="flex justify-between p-2 bg-gray-50 rounded text-sm">
                        <span>{p.concepto_clave} - {p.metodo_pago}</span>
                        <span className="font-bold">{p.moneda} ${p.monto?.toFixed(2)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

// === RENTA CARD COMPONENT ===
const RentaCard = ({ renta, onPago, onDetalle, onContrato, onFactura }) => {
  const totalUSD = renta.total_usd || 0;
  const totalDOP = renta.total_dop || 0;
  const pagadoUSD = renta.pagado_usd || 0;
  const pagadoDOP = renta.pagado_dop || 0;

  return (
    <div data-testid={`renta-card-${renta.id}`} className="border-2 border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-3 border-b flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-black text-amber-800 text-lg">{renta.numero_factura}</p>
          <p className="text-sm font-semibold text-gray-600">{renta.compania_nombre} - <span className="text-amber-600">{renta.pies_rentados} pies</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={renta.estado === 'pagada' ? 'bg-green-100 text-green-800 border-green-300' : 'bg-amber-100 text-amber-800 border-amber-300'}>
            {renta.estado === 'pagada' ? 'PAGADA' : 'ACTIVA'}
          </Badge>
          <Button variant="ghost" size="sm" onClick={onDetalle} className="h-8"><Eye className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={onContrato} className="h-8 text-blue-600" title="Contrato PDF"><FileCheck className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={onFactura} className="h-8 text-green-600" title="Factura PDF"><FileText className="h-4 w-4" /></Button>
        </div>
      </div>
      {/* Conceptos */}
      <div className="p-3">
        <div className="space-y-1">
          {renta.conceptos?.map((c, i) => {
            const pendiente = Math.max(0, c.monto - (c.pagado || 0));
            return (
              <div key={i} className="flex items-center justify-between text-sm p-1.5 rounded hover:bg-gray-50">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] w-10 justify-center">{c.moneda}</Badge>
                  <span className="font-medium">{c.nombre}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">${c.monto?.toFixed(2)}</span>
                  {c.estado === 'pagado' ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <button
                      onClick={() => onPago(c)}
                      className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline"
                      data-testid={`pagar-${c.clave}`}
                    >
                      Pagar (${pendiente.toFixed(2)})
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {/* Totals */}
        <div className="mt-3 pt-2 border-t grid grid-cols-2 gap-2 text-sm">
          <div className="p-2 bg-green-50 rounded">
            <p className="text-xs text-green-600 font-bold">USD</p>
            <p className="font-bold">Total: ${totalUSD.toFixed(2)} | Pagado: <span className="text-green-600">${pagadoUSD.toFixed(2)}</span></p>
          </div>
          <div className="p-2 bg-blue-50 rounded">
            <p className="text-xs text-blue-600 font-bold">DOP (Pesos)</p>
            <p className="font-bold">Total: ${totalDOP.toFixed(2)} | Pagado: <span className="text-blue-600">${pagadoDOP.toFixed(2)}</span></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentaEspacio;
